import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, ArrowRight, BookOpen, UsersThree, Crown, FilePdf,
  CheckCircle, Coins, Warning, Lightbulb, PaperPlaneTilt,
} from "@phosphor-icons/react";

const STEPS = [
  {
    n: 1,
    title: "Decide if a coalition is right for you",
    icon: Lightbulb,
    body: (
      <>
        <p>A coalition makes sense when:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2 text-zinc-300">
          <li>The grant you want is larger than you'd realistically win alone</li>
          <li>You and 1–5 partner orgs have <em>overlapping but distinct</em> missions</li>
          <li>The funder explicitly likes collaborative applications (read the RFP carefully)</li>
          <li>You have the bandwidth to coordinate — coalitions take more upfront work, even if the funded amount is bigger</li>
        </ul>
        <p className="mt-3 text-amber-300/90 text-sm">
          <Warning weight="fill" className="inline w-4 h-4 mr-1" />
          Don't form a coalition just to inflate your numbers. Funders see through it. Form one because the partners genuinely make the work stronger.
        </p>
      </>
    ),
  },
  {
    n: 2,
    title: "Pick your Lead Applicant",
    icon: Crown,
    body: (
      <>
        <p>One org has to be the named applicant. The Lead:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2 text-zinc-300">
          <li>Submits the application as the official applicant of record</li>
          <li>Receives the funds into <em>their</em> bank account</li>
          <li>Distributes funds to partners per the signed Subgrant Agreements</li>
          <li>Reports to the funder on what everyone did</li>
          <li>Carries the audit and IRS responsibility if anything goes sideways</li>
        </ul>
        <p className="mt-3 text-zinc-300">
          <b>Pick the partner with:</b> the most stable 501(c)(3) status, the strongest accounting capacity, and the best grant-writing track record. Not necessarily the org with the biggest mission — the org with the best <em>operations</em>.
        </p>
      </>
    ),
  },
  {
    n: 3,
    title: "Invite your partners on FlashGrants",
    icon: PaperPlaneTilt,
    body: (
      <>
        <p>From your coalition page, click <b>"Invite Partner"</b> for each partner org. We'll email them — they accept by signing into FlashGrants.</p>
        <ul className="list-disc pl-5 space-y-1 mt-2 text-zinc-300">
          <li>Maximum 6 members total (1 lead + 5 partners)</li>
          <li>Partners don't all need their own 501(c)(3) — the Lead can act as fiscal sponsor (see the FAQ below for details + the two big exceptions)</li>
          <li>Wait until everyone accepts before generating documents</li>
        </ul>
      </>
    ),
  },
  {
    n: 4,
    title: "Agree on Scope of Work and Budget Split",
    icon: Coins,
    body: (
      <>
        <p>This is the most important conversation you'll have as a coalition. Hash it out <em>before</em> signing anything.</p>
        <ul className="list-disc pl-5 space-y-1 mt-2 text-zinc-300">
          <li><b>Scope of Work</b> — what each partner will deliver. Be specific. "Run 4 workshops" beats "support programming."</li>
          <li><b>Budget Share %</b> — how the total grant gets split. Must sum to 100%.</li>
          <li>The Lead typically takes 5–15% extra for administrative overhead. Disclose it openly.</li>
          <li>Once agreed, the Lead enters these into FlashGrants. The system enforces the 100% rule.</li>
        </ul>
        <p className="mt-3 text-zinc-400 text-sm italic">
          Rule of thumb: if the conversation about money feels uncomfortable, surface it now. It only gets worse after the funds arrive.
        </p>
      </>
    ),
  },
  {
    n: 5,
    title: "Generate and sign the paperwork",
    icon: FilePdf,
    body: (
      <>
        <p>FlashGrants auto-generates two documents based on your coalition data:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2 text-zinc-300">
          <li><b>Memorandum of Understanding (MOU)</b> — the umbrella agreement signed by ALL members. Lays out roles, responsibilities, distribution schedule, and dispute resolution.</li>
          <li><b>Subgrant Agreement</b> — one per partner. Spells out their specific dollar amount, scope of work, and reporting requirements.</li>
        </ul>
        <p className="mt-3 text-zinc-300">
          Open each document → print → all parties sign → keep one signed copy per organization, in your records.
        </p>
        <p className="mt-3 text-amber-300/90 text-sm">
          <Warning weight="fill" className="inline w-4 h-4 mr-1" />
          These are templates. For grants over ~$25K or anything involving federal money, have an attorney review before signing.
        </p>
      </>
    ),
  },
  {
    n: 6,
    title: "Submit the application — then disburse",
    icon: CheckCircle,
    body: (
      <>
        <p>Only the Lead submits the application on the funder's portal. Partners do not submit anything to the funder directly.</p>
        <p className="mt-3"><b>If awarded:</b></p>
        <ul className="list-disc pl-5 space-y-1 mt-2 text-zinc-300">
          <li>Funder cuts ONE check to the Lead</li>
          <li>Lead disburses to partners per the Subgrant Agreements (default schedule: 50% on award, 40% midpoint, 10% final)</li>
          <li>Each partner submits progress reports to the Lead</li>
          <li>Lead submits the consolidated report to the funder</li>
        </ul>
        <p className="mt-3 text-zinc-300">
          Print the <b>Coalition Setup Checklist</b> from your coalition page and check items off as you go. Keep signed paperwork in a shared folder accessible to all members.
        </p>
      </>
    ),
  },
];

const FAQ = [
  {
    q: "What if a partner doesn't deliver?",
    a: "The Subgrant Agreement should specify what happens. Default: Lead can withhold remaining payments and document the breach. For larger grants, have an attorney write a remedy clause.",
  },
  {
    q: "Do all partners need 501(c)(3) status?",
    a: (
      <>
        <p><b className="text-white">No — but only if the Lead acts as a fiscal sponsor.</b> This is a well-established legal structure. The Lead 501(c)(3) becomes the fiscal sponsor for partners that don't have their own determination letter. From the funder's perspective, it's one tax-exempt org running a partnered program.</p>
        <p className="mt-2"><b className="text-emerald-400">What this means:</b></p>
        <ul className="list-disc pl-5 space-y-1 mt-1 text-zinc-300">
          <li>The funder writes ONE check to the Lead (full amount is tax-deductible)</li>
          <li>Partners can be LLCs, sole proprietors, churches, grassroots collectives, or new nonprofits waiting on their IRS determination letter</li>
          <li>The Lead's audit and IRS responsibility expands — they're legally accountable for how ALL partners spend the money</li>
          <li>The Lead typically charges a <b>5–15% fiscal sponsorship fee</b> on top of regular overhead (industry standard — disclose it openly in the budget split)</li>
        </ul>
        <p className="mt-2 text-amber-300/90 text-sm">
          <Warning weight="fill" className="inline w-4 h-4 mr-1" />
          <b>Two big exceptions where this won't work:</b>
        </p>
        <ul className="list-disc pl-5 space-y-1 mt-1 text-zinc-300 text-sm">
          <li><b>Federal grants</b> — most federal funders require every subrecipient to have their own 501(c)(3) or be a governmental unit. Fiscal sponsorship usually disqualifies you.</li>
          <li><b>Foundations that explicitly exclude it</b> — search the RFP for the words "fiscal sponsor," "subrecipient," or "fiscally sponsored project" before you apply. When in doubt, email the program officer.</li>
        </ul>
      </>
    ),
  },
  {
    q: "Can the Lead take an overhead percentage?",
    a: "Yes, and it's standard. Most coalitions allow the Lead to keep 5–15% of the grant as administrative overhead. Disclose it openly in the budget split.",
  },
  {
    q: "Who handles the audit if there's an issue?",
    a: "The Lead. The Lead is the applicant of record and carries the formal responsibility. This is why picking the right Lead matters more than who has the biggest mission.",
  },
  {
    q: "Can we add a partner after submitting the application?",
    a: "Almost never. Funders treat the application as locked once submitted. Add partners before submitting, not after.",
  },
  {
    q: "What if the application is denied?",
    a: "No money changes hands. The MOU you signed remains valid — you can use it to apply for the next grant together without redoing the paperwork.",
  },
];

const CoalitionPlaybook = () => {
  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-5 py-8 space-y-6">
        <Link to="/coalitions" className="text-xs text-zinc-500 hover:text-emerald-400 inline-flex items-center gap-1" data-testid="back-to-coalitions">
          <ArrowLeft className="w-3 h-3" /> Back to Coalitions
        </Link>

        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-bold tracking-widest uppercase mb-3">
            <BookOpen weight="fill" className="w-3 h-3" /> Playbook
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">How to build a coalition</h1>
          <p className="text-zinc-400 mt-2">
            Joint grant applications are how SC nonprofits win the larger funding most can't reach alone. Here's the full playbook — step by step, no jargon.
          </p>
        </div>

        <Card className="bg-emerald-500/5 border border-emerald-500/30" data-testid="playbook-tldr">
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-widest text-emerald-300 font-mono mb-2 flex items-center gap-1.5">
              <Lightbulb weight="fill" className="w-3 h-3" /> The big idea in one sentence
            </p>
            <p className="text-zinc-200 leading-relaxed">
              <b className="text-white">One org applies for the money. They distribute to partners per a signed agreement. FlashGrants handles the paperwork.</b>
            </p>
          </CardContent>
        </Card>

        {/* The 6 steps */}
        <div className="space-y-4" data-testid="playbook-steps">
          {STEPS.map(s => {
            const Icon = s.icon;
            return (
              <Card key={s.n} className="bg-zinc-950 border-zinc-800">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-sm bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center font-black text-emerald-400 text-lg">{s.n}</div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-white text-lg flex items-center gap-2 mb-2">
                        <Icon weight="duotone" className="w-5 h-5 text-emerald-400" />{s.title}
                      </h3>
                      <div className="text-sm text-zinc-300 leading-relaxed">{s.body}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQ */}
        <div className="pt-4" data-testid="playbook-faq">
          <h2 className="text-xl font-bold tracking-tight mb-3">Common Questions</h2>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <Card key={i} className="bg-zinc-950 border-zinc-800">
                <CardContent className="p-4">
                  <p className="font-bold text-white text-sm mb-1">{item.q}</p>
                  <div className="text-sm text-zinc-400 leading-relaxed">{item.a}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-6 text-center">
            <UsersThree weight="duotone" className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
            <p className="font-bold text-white text-lg mb-1">Ready to start your first coalition?</p>
            <p className="text-zinc-400 text-sm mb-4">
              Or find partners first if you're not sure who to team up with.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              <Link to="/coalitions">
                <Button className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold">
                  Start a Coalition <ArrowRight weight="bold" className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/collaborate">
                <Button variant="ghost" className="text-zinc-300">
                  Find Partners First
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-zinc-600 text-center pt-2">
          The MOU and Subgrant templates on FlashGrants are starting points. For grants over ~$25K or anything federal, have an attorney review.
        </p>
      </div>
    </DashboardLayout>
  );
};

export default CoalitionPlaybook;
